/**
 * 앨범 API
 * - 앨범 CRUD, 목록 조회
 */
import apiClient from './client';

/**
 * Album.outpaintedVariants[] entry — expand outpainting 결과.
 *
 * Expand 모드: jobId 1개당 entry 1개 push. printUnitType 은 미설정 — 사용자가 프런트
 * Step 2 매핑에서 어느 인쇄부에 적용할지 선택. 레거시 combined-sheet 모드에서 누적된
 * entry 들은 printUnitType 이 지정되어 있을 수 있다 (TTL 7일 후 정리).
 */
export interface OutpaintedVariant {
  /** 레거시 combined-sheet 매핑 (FRONT/...). expand 모드에서는 항상 undefined. */
  printUnitType?: string;
  url: string;
  fileId: string;
  sourceJobId: string;
  createdAt: string;
}

export interface Album {
  _id: string;
  userId: string;
  title: string;
  artist: string;
  coverImageId?: string;
  coverImageUrl?: string;
  year?: string;
  genre?: string;
  tracklist: Array<{
    id: string;
    title: string;
    duration?: string;
  }>;
  printUnits: Record<string, any>;
  isPublic: boolean;
  /** 다른 사용자가 이 앨범을 복제할 수 있는지 여부. */
  allowDuplicate?: boolean;
  likes?: string[];
  likeCount?: number;
  sharedWith: string[];
  originalAlbumId?: string;
  originalOwnerId?: string;
  isCopied: boolean;
  copiedBy?: string;
  /** Expand outpainting 결과 누적 (히스토리 보존, 신규 entry 는 printUnitType 미설정). */
  outpaintedVariants?: OutpaintedVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface AlbumListResponse {
  data: Album[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateAlbumData {
  title: string;
  artist: string;
  year?: string;
  genre?: string;
  allowDuplicate?: boolean;
  tracklist?: Array<{
    id: string;
    title: string;
    duration?: string;
  }>;
  printUnits?: Record<string, any>;
  coverImageId?: string;
  coverImageUrl?: string;
  isPublic?: boolean;
}

/**
 * 앨범 생성
 */
export const createAlbum = async (data: CreateAlbumData): Promise<Album> => {
  const response = await apiClient.post<Album>('/albums', data);
  return response.data;
};

/**
 * 내가 생성한 앨범 목록
 */
export const getMyAlbums = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: string;
}): Promise<AlbumListResponse> => {
  const response = await apiClient.get<AlbumListResponse>('/albums/my', { params });
  return response.data;
};

/**
 * 공유받은 앨범 목록
 */
export const getSharedAlbums = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: string;
}): Promise<AlbumListResponse> => {
  const response = await apiClient.get<AlbumListResponse>('/albums/shared', { params });
  return response.data;
};

/**
 * 전체 앨범 목록 (내 앨범 + 공유받은 앨범)
 */
export const getAllAlbums = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: string;
}): Promise<AlbumListResponse> => {
  const response = await apiClient.get<AlbumListResponse>('/albums/all', { params });
  return response.data;
};

/**
 * 앨범 상세 조회
 */
export const getAlbum = async (id: string): Promise<Album> => {
  const response = await apiClient.get<Album>(`/albums/${id}`);
  return response.data;
};

/**
 * 앨범 불러오기 (EditorPage용)
 */
export const loadAlbum = async (id: string): Promise<Album> => {
  const response = await apiClient.get<Album>(`/albums/${id}/load`);
  return response.data;
};

/**
 * 앨범 수정
 */
export const updateAlbum = async (id: string, data: Partial<CreateAlbumData>): Promise<Album> => {
  const response = await apiClient.put<Album>(`/albums/${id}`, data);
  return response.data;
};

/**
 * 앨범 삭제
 */
export const deleteAlbum = async (id: string): Promise<void> => {
  await apiClient.delete(`/albums/${id}`);
};

/**
 * 앨범 복제
 */
export const duplicateAlbum = async (id: string): Promise<Album> => {
  const response = await apiClient.post<Album>(`/albums/${id}/duplicate`);
  return response.data;
};

/**
 * 특정 원본 앨범의 복제본이 내 앨범에 있는지 확인
 */
export const findMyDuplicate = async (originalAlbumId: string): Promise<string | null> => {
  const response = await apiClient.get<{ duplicateId: string | null }>(`/albums/${originalAlbumId}/my-duplicate`);
  return response.data.duplicateId;
};

/**
 * 특정 원본 앨범의 모든 복제본 조회 (미리보기 그리드용 — title/artist/coverImage)
 */
export interface MyDuplicateInfo {
  _id: string;
  title?: string;
  artist?: string;
  coverImage?: string;
}
export const findMyDuplicates = async (originalAlbumId: string): Promise<MyDuplicateInfo[]> => {
  const response = await apiClient.get<{ duplicates: MyDuplicateInfo[] }>(`/albums/${originalAlbumId}/my-duplicates`);
  return response.data.duplicates;
};

/**
 * 북마크 토글
 */
export const toggleBookmark = async (albumId: string): Promise<{ bookmarked: boolean }> => {
  const response = await apiClient.post<{ bookmarked: boolean }>(`/albums/bookmark/${albumId}`);
  return response.data;
};

/**
 * 북마크한 앨범 목록
 */
export interface BookmarkedAlbum {
  _id: string;
  title: string;
  artist: string;
  coverImageUrl?: string;
  isPublic: boolean;
  likeCount: number;
  likes: string[];
  createdAt: string;
  tracklist?: Array<{ id: string; title: string; duration?: string }>;
}

export const getMyBookmarks = async (): Promise<{ data: BookmarkedAlbum[]; total: number }> => {
  const response = await apiClient.get<{ data: BookmarkedAlbum[]; total: number }>('/albums/bookmarks/me');
  return response.data;
};

/**
 * 인기 태그 목록 조회 — 모듈 레벨 dedupe + 60s TTL 캐시.
 *
 * Why: TagInput 다중 마운트 + StrictMode 더블 effect로 동일 GET이 짧은 시간에
 * 4회 이상 발사되어 백엔드 rate-limit(429)을 유발하던 회귀를 차단한다.
 */
const popularTagsCache = new Map<number, { at: number; data: { tag: string; count: number }[] }>();
const popularTagsInflight = new Map<number, Promise<{ tag: string; count: number }[]>>();
const POPULAR_TAGS_TTL_MS = 60_000;

export const getPopularTags = async (limit = 20): Promise<{ tag: string; count: number }[]> => {
  const now = Date.now();
  const cached = popularTagsCache.get(limit);
  if (cached && now - cached.at < POPULAR_TAGS_TTL_MS) return cached.data;
  const inflight = popularTagsInflight.get(limit);
  if (inflight) return inflight;
  const promise = apiClient
    .get<{ tag: string; count: number }[]>('/albums/tags/popular', { params: { limit } })
    .then((response) => {
      popularTagsCache.set(limit, { at: Date.now(), data: response.data });
      popularTagsInflight.delete(limit);
      return response.data;
    })
    .catch((err) => {
      popularTagsInflight.delete(limit);
      throw err;
    });
  popularTagsInflight.set(limit, promise);
  return promise;
};


// ─── 리비전 히스토리 ───────────────────────────────────────────────────────────

export interface Revision {
  _id: string;
  albumId: string;
  seq: number;
  kind: 'snapshot' | 'diff';
  label?: string;
  createdAt: string;
  author?: string;
}

export interface RevisionsListResponse {
  items: Revision[];
  nextCursor: number | null;
}

/**
 * 앨범 리비전 목록 조회 (최신순)
 */
export const getRevisions = async (
  albumId: string,
  params?: { limit?: number; cursor?: number },
): Promise<RevisionsListResponse> => {
  const response = await apiClient.get<RevisionsListResponse>(
    `/albums/${albumId}/revisions`,
    { params },
  );
  return response.data;
};

/**
 * 지정 리비전 시점으로 앨범 복원
 */
export const revertToRevision = async (albumId: string, revisionId: string): Promise<Album> => {
  const response = await apiClient.post<Album>(
    `/albums/${albumId}/revisions/${revisionId}/revert`,
  );
  return response.data;
};

// ─── 앨범 통계 ───────────────────────────────────────────────────────────────

export interface AlbumStatPoint {
  date: string; // YYYY-MM-DD
  views: number;
  likes: number;
}

export interface AlbumStats {
  albumId: string;
  totalViews: number;
  totalLikes: number;
  totalBookmarks: number;
  shareCount: number;
  daily7: AlbumStatPoint[];
  daily30: AlbumStatPoint[];
}

/**
 * 내보내기 월 사용량 조회
 */
export const checkExportUsage = async (): Promise<{ count: number; limit: number | null; exportedAlbumIds: string[] }> => {
  const response = await apiClient.get<{ count: number; limit: number | null; exportedAlbumIds: string[] }>('/albums/exports/usage');
  return response.data;
};

/**
 * 내보내기 기록 로그 (월 한도 체크 포함)
 */
export const logExport = async (albumId: string, format: string): Promise<{ allowed: boolean; remaining: number }> => {
  const response = await apiClient.post<{ allowed: boolean; remaining: number }>('/albums/exports/log', { albumId, format });
  return response.data;
};

/**
 * 앨범 상세 통계 조회
 * - 백엔드 미구현 시 404/501 → null 반환 (graceful fallback)
 */
export const getAlbumStats = async (albumId: string): Promise<AlbumStats | null> => {
  try {
    const response = await apiClient.get<AlbumStats>(`/albums/${albumId}/stats`);
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 501) {
      return null;
    }
    throw error;
  }
};
